/**
 * Timer — show view on the shared Inspector grammar (one-grammar law).
 * Overview leads with the domain-specific schedule (cron in a mono chip),
 * emitted event, status, and the firing metadata (last fired / next due);
 * Relations link the bound Context; ids/timestamps sink to a muted Technical
 * tail; Audit closes the page.
 */
import { useShow, useGo, useNavigation, type HttpError } from "@refinedev/core";
import { Pencil, Compass } from "@/components/ui/icon";
import type { Timer } from "@aos/shared";

import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { StatusBadge } from "@/components/app/StatusBadge";
import {
  Inspector,
  InspectorHeader,
  InspectorSection,
  Field,
  FieldGrid,
  RefLink,
  RelationsBlock,
  AuditBlock,
  ShowPageSkeleton,
  ShowPageError,
  TechnicalId,
} from "@/components/inspector";
import { formatDateTime } from "@/lib/format";

export function TimerShow() {
  const go = useGo();
  const { editUrl } = useNavigation();
  const { query } = useShow<Timer, HttpError>({ resource: "timers" });
  const { data, isLoading, isError, error, refetch } = query;
  const timer = data?.data;

  if (isLoading) return <ShowPageSkeleton />;
  if (isError || !timer) {
    return (
      <ShowPageError
        message={error?.message ?? "Timer not found."}
        onRetry={() => {
          void refetch();
        }}
      />
    );
  }

  return (
    <Inspector>
      <InspectorHeader
        title={timer.name}
        subtitle="A scheduled trigger that emits timer.fired events for the rule engine."
        status={timer.status}
        actions={
          <Button
            variant="outline"
            size="sm"
            onClick={() => go({ to: editUrl("timers", timer.id) })}
          >
            <Pencil className="mr-2 h-4 w-4" /> Edit
          </Button>
        }
      />

      <InspectorSection title="Overview">
        <FieldGrid>
          <Field label="Status">
            <StatusBadge status={timer.status} />
          </Field>
          <Field label="Cron schedule">
            <code className="rounded bg-muted px-1.5 py-0.5 font-mono text-xs">
              {timer.schedule}
            </code>
          </Field>
          <Field label="Emitted event type">
            <Badge variant="outline">{timer.emitted_event_type}</Badge>
          </Field>
          <Field label="Context">
            <RefLink resource="contexts" id={timer.context_id} />
          </Field>
          <Field label="Last fired">{formatDateTime(timer.last_fired_at)}</Field>
          <Field label="Next due">{formatDateTime(timer.next_due_at)}</Field>
        </FieldGrid>
      </InspectorSection>

      <InspectorSection title="Relations">
        <RelationsBlock
          groups={[
            {
              title: "References",
              items: [
                {
                  key: "context",
                  label: "Context",
                  icon: Compass,
                  resource: "contexts",
                  id: timer.context_id,
                },
              ],
            },
          ]}
        />
      </InspectorSection>

      <InspectorSection title="Technical">
        <FieldGrid className="text-muted-foreground">
          <Field label="Id">
            <TechnicalId id={timer.id} />
          </Field>
          <Field label="Created">{formatDateTime(timer.created_at)}</Field>
          <Field label="Updated">{formatDateTime(timer.updated_at)}</Field>
        </FieldGrid>
      </InspectorSection>

      <InspectorSection title="Audit">
        <AuditBlock entityType="timers" entityId={timer.id} />
      </InspectorSection>
    </Inspector>
  );
}

export default TimerShow;
